import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
    Check, Star, Zap, Crown, Shield, Users, CreditCard, Lock
} from 'lucide-react'
import Button from '../components/Button'
import { CupomInput } from '../components/CupomInput'
import { useAuth } from '../contexts/AuthContext'
import { useAssinatura } from '../hooks/useAssinatura'
import { supabase } from '../services/supabase'
import {
    PERIODICIDADE_SUFIXO_PRECO,
    normalizarPeriodicidade,
    obterValorCobrancaPlano,
} from '../utils/periodicidadePix'

interface Plano {
    id: string
    nome: string
    nome_display: string
    descricao: string | null
    preco_mensal: number
    preco_anual: number | null
    periodicidade?: string | null
    recursos: string[] | null
    limite_vendas: number | null
    limite_corretores: number | null
    limite_imoveis: number | null
    suporte_prioritario: boolean
    relatorios_avancados: boolean
    api_access: boolean
    status: string
    ordem_exibicao: number
    data_criacao: string
    data_atualizacao: string
}

interface AfiliadoInfo {
    id: string
    codigo_afiliado: string
    nome_corretor: string
}

export default function Assinar() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { user } = useAuth()
    const { getAssinaturaStatus, loading: assinaturaLoading, assinatura } = useAssinatura()
    const [planos, setPlanos] = useState<Plano[]>([])
    const [afiliadoInfo, setAfiliadoInfo] = useState<AfiliadoInfo | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [cuponsAplicados, setCuponsAplicados] = useState<{ [planoId: string]: any }>({})

    // Verificar se usuário já tem trial ativo e redirecionar para dashboard
    useEffect(() => {
        if (!assinaturaLoading && user) {
            const assinaturaStatus = getAssinaturaStatus()

            // Se tem trial ativo (não expirado), redireciona para dashboard
            if (assinaturaStatus.status === 'trial' && !assinaturaStatus.trial_expirado) {
                navigate('/dashboard')
            }
        }
    }, [assinaturaLoading, user, getAssinaturaStatus, navigate])

    useEffect(() => {
        loadPlanos()
        checkAfiliado()
    }, [])

    const loadPlanos = async () => {
        try {
            const { data, error } = await supabase
                .from('planos')
                .select('*')
                .eq('status', 'ativo')
                .order('ordem_exibicao', { ascending: true })

            if (error) {
                throw error
            }

            const planosProcessados = data?.map(plano => ({
                ...plano,
                recursos: Array.isArray(plano.recursos) ? plano.recursos : []
            })) || []

            setPlanos(planosProcessados)
        } catch (err) {
            setError(`Erro ao carregar planos disponíveis: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
        } finally {
            setLoading(false)
        }
    }

    const checkAfiliado = async () => {
        const refCode = searchParams.get('ref')
        if (!refCode) return

        const { data, error } = await supabase
            .from('afiliados')
            .select(`
                id,
                codigo_afiliado,
                corretor_id,
                corretores (nome)
            `)
            .eq('codigo_afiliado', refCode)
            .single()

        if (error) {
            console.error('Erro ao verificar afiliado:', error)
        }

        if (data) {
            setAfiliadoInfo({
                id: data.id,
                codigo_afiliado: data.codigo_afiliado,
                nome_corretor: (data.corretores as any).nome
            })
        }
    }

    const handleCupomAplicado = (planoId: string, cupomAplicado: any) => {
        setCuponsAplicados(prev => ({
            ...prev,
            [planoId]: cupomAplicado
        }))
    }

    const calcularValorFinal = (plano: Plano): number => {
        const cupom = cuponsAplicados[plano.id]
        if (cupom) {
            return cupom.valorFinal
        }
        return obterValorCobrancaPlano(plano)
    }

    const sufixoPreco = (plano: Plano) =>
        PERIODICIDADE_SUFIXO_PRECO[normalizarPeriodicidade(plano.periodicidade)]

    const handleAssinar = async (planoId: string) => {
        if (!user) {
            navigate('/login?redirect=/assinar')
            return
        }

        // Preparar dados do cupom se aplicado
        const cupom = cuponsAplicados[planoId]
        const cupomData = cupom ? `&cupom=${encodeURIComponent(cupom.cupom.codigo)}` : ''

        navigate(`/pagamento?plano=${planoId}${cupomData}`)
    }

    const getPlanoIcon = (nome: string) => {
        switch (nome) {
            case 'start':
                return <Star className="w-12 h-12 text-blue-500" />
            case 'medium':
                return <Zap className="w-12 h-12 text-purple-500" />
            case 'pro':
                return <Crown className="w-12 h-12 text-yellow-500" />
            default:
                return <Star className="w-12 h-12 text-gray-500" />
        }
    }

    const getPlanoColor = (nome: string) => {
        switch (nome) {
            case 'start':
                return 'border-blue-300 dark:border-blue-700'
            case 'medium':
                return 'border-purple-300 dark:border-purple-700'
            case 'pro':
                return 'border-yellow-300 dark:border-yellow-700'
            default:
                return 'border-gray-300 dark:border-gray-700'
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            Ativar Assinatura
                        </h1>
                        <p className="text-xl text-gray-600 dark:text-gray-400 mb-2">
                            Escolha seu plano e continue usando a plataforma
                        </p>
                        {afiliadoInfo && (
                            <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-4 py-2 rounded-lg">
                                <Users className="w-4 h-4" />
                                <span className="text-sm">
                                    Indicado por: {afiliadoInfo.nome_corretor}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-red-800 dark:text-red-400">{error}</p>
                    </div>
                </div>
            )}

            {/* Planos */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {planos.map((plano) => (
                        <div
                            key={plano.id}
                            className={`bg-white dark:bg-gray-800 rounded-2xl border-2 ${getPlanoColor(plano.nome)} p-8 relative transition-all duration-300 hover:shadow-xl ${plano.nome === 'pro' ? 'scale-105' : ''}`}
                        >
                            {/* Badge para plano Pro */}
                            {plano.nome === 'pro' && (
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1">
                                        <Crown className="w-4 h-4" />
                                        Mais Popular
                                    </div>
                                </div>
                            )}

                            {/* Header do Plano */}
                            <div className="text-center mb-8">
                                <div className="flex justify-center mb-4">
                                    {getPlanoIcon(plano.nome)}
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                    {plano.nome_display}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-4">
                                    {plano.descricao}
                                </p>
                                <div className="space-y-2">
                                    {cuponsAplicados[plano.id] ? (
                                        <>
                                            <div className="flex items-center justify-center gap-2">
                                                <span className="text-2xl font-medium text-gray-500 dark:text-gray-400 line-through">
                                                    {formatCurrency(obterValorCobrancaPlano(plano))}
                                                </span>
                                                <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-2 py-1 rounded text-xs font-medium">
                                                    -R$ {cuponsAplicados[plano.id].desconto.toFixed(2)}
                                                </span>
                                            </div>
                                            <p className="text-5xl font-extrabold text-green-600 dark:text-green-400">
                                                {formatCurrency(calcularValorFinal(plano))}
                                                <span className="text-xl font-medium text-green-500 dark:text-green-300">{sufixoPreco(plano)}</span>
                                            </p>
                                        </>
                                    ) : (
                                        <p className="text-5xl font-extrabold text-gray-900 dark:text-gray-100">
                                            {formatCurrency(obterValorCobrancaPlano(plano))}
                                            <span className="text-xl font-medium text-gray-600 dark:text-gray-400">{sufixoPreco(plano)}</span>
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Recursos do Plano */}
                            <div className="space-y-4 mb-6">
                                {Array.isArray(plano.recursos) && plano.recursos.map((recurso: string, index: number) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                                        <span className="text-gray-700 dark:text-gray-300">{recurso}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Campo de Cupom */}
                            <div className="mb-6">
                                <CupomInput
                                    planoSelecionado={plano.nome}
                                    valorOriginal={obterValorCobrancaPlano(plano)}
                                    onCupomAplicado={(cupom) => handleCupomAplicado(plano.id, cupom)}
                                />
                            </div>

                            {/* Botão de Ação */}
                            {assinatura?.plano?.nome === plano.nome ? (
                                // Se é o plano atual do usuário
                                <>
                                    <div className="mb-3 text-center px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <span className="text-xs font-medium text-yellow-800">Seu Plano Atual</span>
                                    </div>
                                    
                                    {/* Se a assinatura estiver expirada/cancelada OU trial expirado, mostra botão de renovação */}
                                    {(assinatura?.status === 'expirada' || 
                                      assinatura?.status === 'cancelada' || 
                                      getAssinaturaStatus().trial_expirado) && (
                                        <Button
                                            variant="btn_add"
                                            onClick={() => handleAssinar(plano.id)}
                                            className="w-full mb-3"
                                        >
                                            Continuar com meu plano →
                                        </Button>
                                    )}
                                    
                                    {/* Botão desabilitado para plano ativo ou trial ativo */}
                                    {(assinatura?.status === 'ativa' || 
                                      (assinatura?.status === 'trial' && !getAssinaturaStatus().trial_expirado)) && (
                                        <Button
                                            variant="btn_secondary"
                                            disabled={true}
                                            className="w-full"
                                        >
                                            Plano Ativo
                                        </Button>
                                    )}
                                </>
                            ) : (
                                // Se não é o plano atual, mostra botão normal
                                <Button
                                    variant={plano.nome === 'pro' ? 'btn_add' : 'btn_secondary'}
                                    onClick={() => handleAssinar(plano.id)}
                                    className="w-full"
                                >
                                    Assinar Agora →
                                </Button>
                            )}

                            {/* Garantia */}
                            <div className="mt-4 text-center">
                                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                    <Shield className="w-4 h-4" />
                                    <span>Cancelamento a qualquer momento</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Informações de Pagamento */}
                <div className="mt-16 text-center">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8">
                        Formas de Pagamento
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                        <div className="flex items-center justify-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-300 dark:border-blue-700">
                            <CreditCard className="w-8 h-8 text-blue-600" />
                            <span className="font-medium text-gray-900 dark:text-gray-100">Cartão de Crédito</span>
                        </div>
                        <div className="flex items-center justify-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-300 dark:border-green-700">
                            <Lock className="w-8 h-8 text-green-600" />
                            <span className="font-medium text-gray-900 dark:text-gray-100">PIX Automático</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    )
}
